import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid, Button } from 'components/ui';
import { useQuery } from '@apollo/client/react';
import { useModal } from 'contexts/modal';
import { AddIcon } from 'components/uiIcons';
import PlacesQuery from 'queries/place/index';
import PlaceAndPlacesQuery from 'queries/place/index_and_show';
import OptionableAutocomplete from 'components/form/autocomplete/optionable';
import CreatePlace from 'components/place/modal/create';

const Place = ({
  id,
  label,
  name,
  onChange,
  onBlur,
  value,
  error,
  helperText,
  setValue,
  autoFocus,
}) => {
  const { addModal, removeModal } = useModal();
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState(null);
  const [defaultValue, setDefaultValue] = useState(null);
  const hydratedRef = useRef(false);

  const {
    data = {
      space: { overPlacesLimit: false },
      places: { places: [], placesTotal: 0 },
      place: null,
    },
    loading,
  } = useQuery(value ? PlaceAndPlacesQuery : PlacesQuery, {
    variables: { page: 1, name: searchValue, placeId: value },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!loading && !hydratedRef.current && data?.place) {
      hydratedRef.current = true;
      setDefaultValue({ label: data.place.name, value: data.place.id });
    }
  }, [data, loading]);

  const options = useMemo(
    () => data.places.places.map((place) => ({ label: place.name, value: place.id })),
    [data]
  );

  const onChangeInternal = useCallback(
    (evt, value) => {
      setDefaultValue(value);
      onChange(evt, value);
    },
    [onChange]
  );

  const addButton = () => {
    if (data.space.overPlacesLimit) return null;

    return (
      <Button
        onClick={() =>
          addModal(
            <CreatePlace
              handleClose={() => removeModal()}
              name={searchValue}
              onSuccess={(place) => {
                setDefaultValue({ label: place.name, value: place.id });
                removeModal();
                setValue(name, place.id);
              }}
            />,
            { disableAutoFocus: true, disableRestoreFocus: true }
          )
        }
        variant="contained"
      >
        <AddIcon />
      </Button>
    );
  };

  return (
    <OptionableAutocomplete
      autoFocus={autoFocus}
      id={id}
      label={label}
      options={options}
      name={name}
      onChange={onChangeInternal}
      loadingText={intl.formatMessage({ id: 'autocomplete.place.loading' })}
      noOptionsText={
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <FormattedMessage id="autocomplete.place.no_options" />
          {addButton()}
        </Grid>
      }
      value={defaultValue}
      onBlur={onBlur}
      loading={loading}
      error={error}
      helperText={helperText}
      onSearch={setSearchValue}
    />
  );
};

export default Place;
